import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Close } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { AppDispatch, RootState } from '../../store';
import { fetchSystemMessage, dismissCurrentMessage } from '../../store/slices/systemMessageSlice';

const SystemMessageBanner: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { data, dismissedText } = useSelector((state: RootState) => state.systemMessage);

    useEffect(() => {
        dispatch(fetchSystemMessage());
    }, [dispatch]);

    // Hide banner if not enabled or message is empty
    if (!data || !data.enabled || !data.messageText || data.messageText.trim() === '') {
        return null;
    }

    // Hide banner if user dismissed this exact text content
    if (dismissedText === data.messageText) {
        return null;
    }

    const isRed = data.color === 'RED';

    // Apply clear background and border styles
    const colorStyles = isRed ? 'bg-red-600 text-white' : 'bg-dark-primary text-white';

    // Helper component to render repeating text items with clean spacing
    const TickerItem = () => (
        <span className="pr-16 font-semibold text-sm tracking-wide shrink-0">
            {data.messageText}
        </span>
    );

    return (
        <>
            {/* Professional GPU-accelerated ticker animation with seamless -50% loop */}
            <style>{`
        @keyframes systemMarquee {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        .animate-system-marquee {
          display: flex;
          width: max-content;
          animation: systemMarquee 60s linear infinite;
          will-change: transform;
        }
        .animate-system-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>

            {/* Using flex layout without absolute positioning ensures text NEVER goes under the button */}
            {/* Added px-2 to ensure visually balanced padding on both the left and right edges */}
            <div className={`w-full h-10 flex items-center shadow-md pl-4 pr-1 ${colorStyles}`}>

                {/* Text Wrapper - flex-1 takes all available space except what the button needs */}
                <div className="flex-1 overflow-hidden whitespace-nowrap h-full flex items-center">
                    {data.animated ? (
                        <div className="animate-system-marquee">
                            {/* Track Group A (50% of total width) */}
                            <div className="flex items-center shrink-0">
                                <TickerItem />
                                <TickerItem />
                                <TickerItem />
                                <TickerItem />
                            </div>
                            {/* Track Group B (Identical clone for seamless loop) */}
                            <div className="flex items-center shrink-0">
                                <TickerItem />
                                <TickerItem />
                                <TickerItem />
                                <TickerItem />
                            </div>
                        </div>
                    ) : (
                        <div className="font-semibold text-sm tracking-wide text-center w-full truncate pr-4">
                            {data.messageText}
                        </div>
                    )}
                </div>

                {/* Close Button - flex-shrink-0 prevents it from resizing, stays clearly on the right */}
                {/* Added pl-4 to keep a safe distance between text and button */}
                <div className="flex-shrink-0 pl-1">
                    <IconButton
                        onClick={() => dispatch(dismissCurrentMessage())}
                        size="small"
                        aria-label="Close notification"
                        className="!text-white hover:!bg-black/20"
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </div>
            </div>
        </>
    );
};

export default SystemMessageBanner;